import { Link } from 'react-router-dom';
import { comparisonPlatforms } from '@/mocks/categories';

export default function ContentSection() {
  return (
    <>
      <section id="about" className="w-full py-14 md:py-20 bg-background-100">
        <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold tracking-wide uppercase mb-3">
              What You Need to Know
            </span>
            <h2 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">
              What Is Hipobuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              <strong>Hipobuy Spreadsheet</strong> is a structured product discovery resource that helps shoppers organize products, compare options, and find useful buying information faster. Instead of searching through endless pages, spreadsheet-based shopping guides provide organized categories, product references, and buying insights — all in one place.
            </p>
          </div>

          <div className="overflow-x-auto max-w-3xl mx-auto">
            <table className="w-full border-collapse rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-primary-500">
                  <th className="px-5 py-3.5 text-left text-sm font-semibold text-background-50 whitespace-nowrap">Feature</th>
                  <th className="px-5 py-3.5 text-left text-sm font-semibold text-background-50 whitespace-nowrap">Traditional Search</th>
                  <th className="px-5 py-3.5 text-left text-sm font-semibold text-background-50 whitespace-nowrap">Spreadsheet Research</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                <tr className="bg-background-50">
                  <td className="px-5 py-3 text-sm font-medium text-foreground-800">Product Discovery</td>
                  <td className="px-5 py-3 text-sm text-foreground-600">Slow &amp; fragmented</td>
                  <td className="px-5 py-3 text-sm text-primary-700 font-medium">Organized &amp; fast</td>
                </tr>
                <tr className="bg-background-50/60">
                  <td className="px-5 py-3 text-sm font-medium text-foreground-800">Comparison</td>
                  <td className="px-5 py-3 text-sm text-foreground-600">Difficult &amp; manual</td>
                  <td className="px-5 py-3 text-sm text-primary-700 font-medium">Easy &amp; structured</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-5 py-3 text-sm font-medium text-foreground-800">Category Browsing</td>
                  <td className="px-5 py-3 text-sm text-foreground-600">Limited filtering</td>
                  <td className="px-5 py-3 text-sm text-primary-700 font-medium">Comprehensive &amp; organized</td>
                </tr>
                <tr className="bg-background-50/60">
                  <td className="px-5 py-3 text-sm font-medium text-foreground-800">Research Time</td>
                  <td className="px-5 py-3 text-sm text-foreground-600">Hours of scrolling</td>
                  <td className="px-5 py-3 text-sm text-primary-700 font-medium">Minutes to scan</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="max-w-3xl mx-auto mt-8 text-center">
            <p className="text-sm text-foreground-500 mb-4">
              Thousands of shoppers use spreadsheet-based guides every day to find better products with less effort. Whether you are hunting for sneakers, hoodies, or accessories, a well-organized hipobuy spreadsheet saves you time and helps you discover items you might otherwise miss.
            </p>
            <Link
              to="/hipobuy-spreadsheet-guide"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              Learn more in our complete guide
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <section id="compare" className="w-full py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-3">
              Platform Comparison
            </span>
            <h2 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-3">
              Why Users Search for Spreadsheet Guides
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Different platforms offer different strengths. Here is how the most popular spreadsheet resources compare.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {comparisonPlatforms.map((platform, idx) => (
              <div
                key={idx}
                className={`p-5 md:p-6 rounded-lg border transition-colors ${
                  idx === 0
                    ? 'bg-primary-50 border-primary-200'
                    : 'bg-background-100 border-background-200/70 hover:border-primary-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      idx === 0 ? 'bg-primary-500' : 'bg-accent-500'
                    }`}
                  ></div>
                  <h4 className="font-heading text-lg text-foreground-900">
                    {platform.name}
                  </h4>
                </div>
                <p className="text-xs md:text-sm text-foreground-600 mb-4">
                  <strong>Best for:</strong> {platform.bestFor}
                </p>
                <ul className="space-y-2">
                  {platform.features.map((feat, fIdx) => (
                    <li key={fIdx} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-xs text-foreground-400 italic max-w-2xl mx-auto">
              This website is an independent educational resource. We are not affiliated with Hipobuy, Kakobuy, CSSBuy, Superbuy, Oopbuy or any marketplace platform.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}